'use client'

import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Autocomplete from '@mui/material/Autocomplete'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { PALETTE } from '../../styles/constants'
import { MUI_THEME } from '../../styles/muiTheme'
import styles from './styles.module.css'
import { $background, $textPrimary } from '../../styles/colors/semantics'
import { $neutralGray80 } from '../../styles/colors/primitives'
import { useMounted } from '../../utils/hooks/useMounted'

function MyAutocomplete({ isLoading, options, getOptionLabel, filterOptions, value, onChange, size = 'small', disableClearable = true,
                          inputTextColor = PALETTE.gray100, popupIndicatorColor = PALETTE.gray100, popupIndicatorWidth = 24, fontSizeAdjust = 0, transparentBorder,
                          isUpperCase, dark, noPadding, ariaLabel = 'Select option', autoComplete, name, placeholder, staticWidth, fullWidth, staticWidthLg }) {
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)
  const inputCloneRef = useRef(null)
  // #418: the picker's `disabled={isLoading}` flips a DOM attribute driven by the
  // global isLoading flag, which is true at SSR under backend latency but false on
  // the client's first render -> hydration mismatch. Only apply it post-mount so
  // the SSR HTML and the client's first render agree on the disabled attribute.
  const mounted = useMounted()

  filterOptions = filterOptions || ((options, state) => {
    const input = state.inputValue.toLowerCase()
    const startsWith = (o) => getOptionLabel(o).toLowerCase().startsWith(input)
    const includes = (o) => getOptionLabel(o).toLowerCase().includes(input)

    // prioritize options that "start with" before options that "include" (but do not "start with")
    return options.filter(startsWith).concat(
      options.filter(o => includes(o) && !startsWith(o))
    )
  })

  const onChangeWrapper = (event, newValue) => newValue && onChange(newValue)

  // Auto-select when browser autofill matches an option exactly
  const onInputChangeWrapper = (event, inputValue, reason) => {
    if (reason === 'input' && inputValue) {
      const exactMatch = options.find(o =>
        getOptionLabel(o).toLowerCase() === inputValue.toLowerCase()
      )
      if (exactMatch) {
        onChange(exactMatch)
      }
    }
  }

  const renderInput = params =>
    <>
      <span className={styles.textFieldClone} ref={inputCloneRef}/>
      <TextField {...params} variant="standard" inputRef={inputRef} placeholder={placeholder} inputProps={{ ...params.inputProps, 'aria-label': ariaLabel, autoComplete: autoComplete || 'off', name: name }}/>
    </>

  const theme = createTheme({
    components: {
      MuiAutocomplete: {
        defaultProps: {
          size: size,
          disableClearable: disableClearable
        },
        styleOverrides: {
          inputRoot: {
            // set right padding so input ends where popupIndicator starts
            paddingRight: popupIndicatorWidth + 'px !important',
            paddingBottom: '0',

            // change border color when unfocused
            '&:before': {
              borderColor: transparentBorder ? 'transparent' : PALETTE.gray70,

              // prevent autocomplete disabled state to set border style to 'dotted'
              borderBottomStyle: 'solid'
            },

            // change border color when hovered
            '&:hover:before': {
              borderColor: transparentBorder ? 'transparent' : PALETTE.gray70,
              borderBottomStyle: 'solid'
            },

            // change border color when element "loses" focus
            '&:after': {
              borderColor: transparentBorder ? 'transparent' : PALETTE.gray70,
              borderBottomStyle: 'solid'
            },

            // update disabled styling
            '& .Mui-disabled': {
              '-webkit-text-fill-color': inputTextColor,
              color: inputTextColor,
              opacity: 0.5
            },
          },
          input: {
            // remove right-padding, so text is closer to popupIndicator
            paddingRight: '0 !important',
            color: inputTextColor,
            fontWeight: 500,
            fontFamily: 'Kanit',
            textTransform: isUpperCase ? 'uppercase' : 'none',

            // MUI media queries, adjust text for screen sizes
            [MUI_THEME.breakpoints.up('xl')]: {
              fontSize: (18 + fontSizeAdjust) + 'px'
            },
            [MUI_THEME.breakpoints.between('lg', 'xl')]: {
              fontSize: (16 + fontSizeAdjust) + 'px'
            },
            [MUI_THEME.breakpoints.down('lg')]: {
              fontSize: (16 + fontSizeAdjust) + 'px'
            }
          },
          option: {
            '&.MuiAutocomplete-option:hover': {
              backgroundColor: dark ? $neutralGray80 : $textPrimary.dark
            },
            '&.MuiAutocomplete-option.Mui-focused': {
              backgroundColor: dark ? $neutralGray80 : $textPrimary.dark
            },
            color: dark ? $textPrimary.dark : $textPrimary.light,
            padding: '12px',
            fontWeight: 400,
            fontFamily: 'Kanit',
            textTransform: isUpperCase ? 'uppercase' : 'none',
            // always take up as much height as content
            minHeight: 'auto',
            [MUI_THEME.breakpoints.up('xl')]: {
              fontSize: (16 + fontSizeAdjust) + 'px'
            },
            [MUI_THEME.breakpoints.between('lg', 'xl')]: {
              fontSize: (14 + fontSizeAdjust) + 'px'
            },
            [MUI_THEME.breakpoints.down('lg')]: {
              fontSize: (14 + fontSizeAdjust) + 'px'
            }
          },
          popupIndicator: {
            color: popupIndicatorColor,

            // style svg used as popupIcon
            '& svg': {
              width: popupIndicatorWidth + 'px',
              height: popupIndicatorWidth + 'px'
            }
          },
          // style container holding all options
          paper: {
            width: 'max-content',
            maxWidth: '300px',
            backgroundColor: dark ? $background.dark : $background.light,

            [MUI_THEME.breakpoints.down('sm')]: {
              maxWidth: 'calc(100% + 16px)'
            }
          },
        },
      },
    },
  })

  // CLS (genuine-0, /us/shop ship-to picker): `staticWidth` opts OUT of the JS
  // auto-hug measurement below. That measurement runs POST-PAINT (useEffect) and
  // writes `wrapperRef.style.width`, so the box always reflows once from its SSR
  // intrinsic width to the measured width AFTER first paint (a horizontal shift of
  // the right-anchored popupIndicator) — and reflows AGAIN whenever `value` or the
  // font changes post-mount (placeholder -> country, desktop -> mobile font). The
  // SSR HTML cannot carry the measured pixel width (no server font metrics), so no
  // amount of SSR seeding removes that first reflow. `staticWidth` instead sizes
  // the box with a fixed CSS width (styles.staticWidth), so the box is
  // value/font-INDEPENDENT and identical at SSR paint, hydration and every
  // post-mount overlay: the arrow never moves -> CLS 0. This is the "stable
  // reserved box" path (robust to the cookie/geo shipping-country disagreeing with
  // the URL region, which `overlayShippingCountry` allows). Scoped via the prop so
  // the other 4 CountriesAutocomplete / StatesAutocomplete call sites keep the hug.
  //
  // CLS (genuine-0, /checkout billing pickers): `fullWidth` is the FORM variant of
  // the same opt-out. The billing country/state pickers live in a fixed-width form
  // cell (AddressInput's .autocompleteWrapper), so instead of a fixed px hug they
  // fill their container (styles.fullWidth = width:100%): the box has a
  // DETERMINISTIC width (the cell's) from first paint and the right-anchored arrow
  // sits at the cell's fixed right edge and CANNOT move on value/font change. Like
  // `staticWidth`, it skips the post-paint JS measurement so nothing reflows -> CLS
  // 0. Both flags default off, so the hug call sites are untouched.
  //
  // CLS (genuine-0, footer "Shipping to" picker): `staticWidthLg` is the FIXED-box
  // opt-out for the FOOTER country picker — the sole element that shifts /checkout.
  // An empty-cart /checkout (what the CLS gate measures) is a short page, so the
  // footer sits in the first viewport and its post-mount value-driven auto-hug
  // reflow scores CLS; the billing form is not even mounted in that state. Same
  // "value/font-independent fixed box + skip the JS measurement" idea as
  // `staticWidth`, but with its OWN width class (styles.staticWidthLg) that carries
  // NO font override — the footer keeps its large `fontSizeAdjust={2}` (18px) type,
  // whereas `.staticWidth` deliberately shrinks the shop input to 11px on mobile.
  // Sized (156px) to the footer's default "United States" render so the common case
  // is pixel-identical to today; shorter names simply stop hugging (arrow pinned to
  // the fixed right edge) -> no reflow. Defaults off, so other call sites are untouched.
  useEffect(() => {
    if (staticWidth || fullWidth || staticWidthLg) return
    resizeInputOnValueChanged(value)
  },[value, staticWidth, fullWidth, staticWidthLg])

  const resizeInputOnValueChanged = (value) => {
    // achieve auto-scaling of Autocomplete input to the width of its value, read more here: https://noto.li/Z7cir5
    const inputStyles = window.getComputedStyle(inputRef.current, null)

    inputCloneRef.current.style.fontFamily = inputStyles.getPropertyValue('font-family')
    inputCloneRef.current.style.fontSize = inputStyles.getPropertyValue('font-size')
    inputCloneRef.current.style.fontWeight = inputStyles.getPropertyValue('font-weight')
    inputCloneRef.current.style.letterSpacing = inputStyles.getPropertyValue('letter-spacing')

    // When there's no value, size the input to the placeholder ("SELECT
    // COUNTRY") so it isn't clipped to the collapsed empty width.
    const optionLabel = getOptionLabel(value) || placeholder || ''
    inputCloneRef.current.textContent = isUpperCase ? optionLabel.toUpperCase() : optionLabel
    wrapperRef.current.style.width = (inputCloneRef.current.offsetWidth + popupIndicatorWidth + 20) + 'px'
  }

  const wrapperClassName = [
    styles.wrapper,
    noPadding ? '' : styles.padding,
    staticWidth ? styles.staticWidth : '',
    fullWidth ? styles.fullWidth : '',
    staticWidthLg ? styles.staticWidthLg : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClassName} ref={wrapperRef}>
      <ThemeProvider theme={theme}>
        <Autocomplete
          options={options}
          getOptionLabel={getOptionLabel}
          filterOptions={filterOptions}
          value={value}
          renderInput={renderInput}
          onChange={onChangeWrapper}
          onInputChange={onInputChangeWrapper}
          autoHighlight={true}
          blurOnSelect={true}
          disabled={mounted && isLoading}
        />
      </ThemeProvider>
    </div>
  )
}

MyAutocomplete.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  options: PropTypes.array.isRequired,
  getOptionLabel: PropTypes.func.isRequired, // example: (option) => option.name
  filterOptions: PropTypes.func,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,

  size: PropTypes.string,
  disableClearable: PropTypes.bool,
  inputTextColor: PropTypes.string,
  popupIndicatorColor: PropTypes.string,
  popupIndicatorWidth: PropTypes.number,

  // use this to adjust font size across all screen sizes
  // example: fontSizeAdjust value of 2 will increase font by 2 across all screen sizes, a value of -2 will decrease it
  fontSizeAdjust: PropTypes.number,

  // accessibility label for screen readers
  ariaLabel: PropTypes.string,

  // CLS: when true, size the control with a fixed CSS width (styles.staticWidth)
  // and SKIP the post-paint JS auto-hug measurement, so the box (and its
  // right-anchored popupIndicator) never reflows on value/font change -> CLS 0.
  // Used by the /us/shop ship-to picker (see the useEffect note above).
  staticWidth: PropTypes.bool,

  // CLS: like `staticWidth`, but sizes the control to width:100% (styles.fullWidth)
  // instead of a fixed px — the FORM variant, for pickers that fill a fixed-width
  // form cell (the /checkout billing country/state pickers). Also skips the JS
  // auto-hug measurement so the box (and its arrow) is deterministic from first
  // paint -> CLS 0. Defaults off; mutually exclusive with staticWidth in practice.
  fullWidth: PropTypes.bool,

  // CLS: like `staticWidth` (fixed CSS width + skip the JS auto-hug), but uses the
  // styles.staticWidthLg class, which has NO mobile font override — so the box is
  // value/font-independent yet the input keeps its large footer font. Used by the
  // footer "Shipping to" country picker (see the useEffect note above). Defaults off.
  staticWidthLg: PropTypes.bool
}

export default MyAutocomplete
